"use client";
import type { ReactNode } from "react";
import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, buttonVariants, Separator } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import Drawer from "@mui/material/Drawer";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { ClassValue } from "clsx";
import {
  Book,
  Cloud,
  Discord,
  EvPlug,
  Github,
  HeadsetHelp,
  Heart,
  Home,
  InfoCircle,
  MessageAlert,
  MoneySquare,
  MoreHorizCircle,
  MultiplePages,
  Page,
  Rocket,
  Server,
  SidebarCollapse,
  SidebarExpand,
  StatsUpSquare,
  Tools,
  X as TwitterX,
  Youtube
} from "iconoir-react";
import { useAtom } from "jotai";
import Link from "next/link";

import { SITE_CONFIG } from "@src/config/site.config";
import { useSettings } from "@src/context/SettingsProvider";
import { useFlag } from "@src/hooks/useFlag";
import { useUser } from "@src/hooks/useUser";
import sdlStore from "@src/store/sdlStore";
import type { ISidebarGroupMenu, ISidebarRoute } from "@src/types";
import { UrlService } from "@src/utils/urlUtils";
import { MobileSidebarUser } from "./MobileSidebarUser";
import { ModeToggle } from "./ModeToggle";
import { SidebarGroupMenu } from "./SidebarGroupMenu";

type Props = {
  children?: ReactNode;
  isMobileOpen: boolean;
  handleDrawerToggle: () => void;
  onOpenMenuClick: () => void;
  isNavOpen: boolean;
  mdDrawerClassName?: ClassValue;
};

const DRAWER_WIDTH = 240;
const CLOSED_DRAWER_WIDTH = 57;

export const Sidebar: React.FunctionComponent<Props> = ({ isMobileOpen, handleDrawerToggle, isNavOpen, onOpenMenuClick, mdDrawerClassName }) => {
  const intl = useIntl();
  const { settings } = useSettings();
  const [, setDeploySdl] = useAtom(sdlStore.deploySdl);
  const muiTheme = useMuiTheme();
  const smallScreen = useMediaQuery(muiTheme.breakpoints.down("md"));
  const { user } = useUser();
  const isAuthenticated = !!user?.userId;
  const isAlertsEnabled = useFlag("alerts");

  const mainRoutes = useMemo(() => {
    const routes: ISidebarRoute[] = isAuthenticated
      ? [
          {
            title: intl.formatMessage({ id: "layout.sidebar.home", defaultMessage: "Home" }),
            icon: props => <Home {...props} />,
            url: UrlService.home(),
            activeRoutes: [UrlService.home()]
          },
          {
            title: intl.formatMessage({ id: "layout.sidebar.deployments", defaultMessage: "Deployments" }),
            icon: props => <Cloud {...props} />,
            url: UrlService.deploymentList(),
            activeRoutes: [UrlService.deploymentList(), "/deployments", "/new-deployment"]
          },
          {
            title: intl.formatMessage({ id: "layout.sidebar.templates", defaultMessage: "Templates" }),
            icon: props => <MultiplePages {...props} />,
            url: UrlService.templates(),
            activeRoutes: [UrlService.templates()]
          },
          {
            title: intl.formatMessage({ id: "layout.sidebar.sdlbuilder", defaultMessage: "SDL Builder" }),
            icon: props => <Tools {...props} />,
            url: UrlService.sdlBuilder(),
            activeRoutes: [UrlService.sdlBuilder()],
            testId: "sidebar-sdl-builder-link"
          },
          {
            title: intl.formatMessage({ id: "layout.sidebar.providers", defaultMessage: "Providers" }),
            icon: props => <Server {...props} />,
            url: UrlService.providers(),
            activeRoutes: [UrlService.providers()]
          }
        ]
      : [];

    if (isAlertsEnabled && user?.userId) {
      routes.push({
        title: intl.formatMessage({ id: "layout.sidebar.alerts", defaultMessage: "Alerts" }),
        icon: props => <MessageAlert {...props} />,
        url: UrlService.alerts(),
        activeRoutes: [UrlService.alerts()]
      });
    }

    return routes;
  }, [isAlertsEnabled, isAuthenticated, user?.userId, intl]);

  const routeGroups: ISidebarGroupMenu[] = useMemo(
    () => [
      {
        hasDivider: false,
        routes: mainRoutes
      }
    ],
    [mainRoutes]
  );

  const extraRoutes: ISidebarGroupMenu[] = useMemo(() => {
    // 社交链接源自 SITE_CONFIG.social；URL 为空的条目隐藏，整组为空时不显示 "Follow" 入口。
    const socialCandidates: Array<ISidebarRoute & { url: string }> = [
      {
        title: intl.formatMessage({ id: "layout.sidebar.social.x", defaultMessage: "{brand} on X" }, { brand: SITE_CONFIG.brandShortName }),
        icon: props => <TwitterX {...props} />,
        url: SITE_CONFIG.social.twitter
      },
      {
        title: intl.formatMessage({ id: "layout.sidebar.social.discord", defaultMessage: "{brand} Discord" }, { brand: SITE_CONFIG.brandShortName }),
        icon: props => <Discord {...props} />,
        url: SITE_CONFIG.social.discord
      },
      {
        title: intl.formatMessage({ id: "layout.sidebar.social.youtube", defaultMessage: "{brand} Youtube" }, { brand: SITE_CONFIG.brandShortName }),
        icon: props => <Youtube {...props} />,
        url: SITE_CONFIG.social.youtube
      },
      {
        title: intl.formatMessage({ id: "layout.sidebar.social.github", defaultMessage: "{brand} GitHub" }, { brand: SITE_CONFIG.brandShortName }),
        icon: props => <Github {...props} />,
        url: SITE_CONFIG.social.github
      }
    ];

    const socialRoutes: ISidebarRoute[] = socialCandidates
      .filter(route => !!route.url)
      .map(route => ({ ...route, target: "_blank", rel: "noreferrer noopener" }));

    const followRoute: ISidebarRoute = {
      title: intl.formatMessage({ id: "layout.sidebar.follow", defaultMessage: "Follow {brand}" }, { brand: SITE_CONFIG.brandShortName }),
      icon: props => <Heart {...props} />,
      hoveredRoutes: [
        {
          hasDivider: false,
          routes: socialRoutes
        }
      ]
    };

    const routes: ISidebarGroupMenu[] = [
      {
        hasDivider: false,
        routes: [
          ...(socialRoutes.length > 0 ? [followRoute] : []),
          {
            title: intl.formatMessage({ id: "layout.sidebar.resources", defaultMessage: "Resources" }),
            icon: props => <InfoCircle {...props} />,
            hoveredRoutes: [
              {
                hasDivider: false,
                routes: [
                  {
                    title: SITE_CONFIG.brandName,
                    icon: props => <Cloud {...props} />,
                    url: SITE_CONFIG.domains.marketing,
                    activeRoutes: [],
                    target: "_blank"
                  },
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.stats", defaultMessage: "Stats" }),
                    icon: props => <StatsUpSquare {...props} />,
                    url: SITE_CONFIG.domains.stats,
                    activeRoutes: [],
                    target: "_blank",
                    hasDivider: true
                  },
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.pricecompare", defaultMessage: "Price Compare" }),
                    icon: props => <MoneySquare {...props} />,
                    url: UrlService.priceCompare(),
                    activeRoutes: [UrlService.priceCompare()]
                  },
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.apidocs", defaultMessage: "API Docs" }),
                    icon: props => <EvPlug {...props} />,
                    url: SITE_CONFIG.domains.docs,
                    activeRoutes: [],
                    target: "_blank"
                  },
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.docs", defaultMessage: "Docs" }),
                    icon: props => <Book {...props} />,
                    url: SITE_CONFIG.domains.docs,
                    activeRoutes: [],
                    target: "_blank"
                  },
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.faq", defaultMessage: "FAQ" }),
                    icon: props => <Page {...props} />,
                    url: UrlService.faq(),
                    activeRoutes: [UrlService.faq()]
                  }
                ]
              }
            ]
          },
          {
            title: intl.formatMessage({ id: "layout.sidebar.helpfromexpert", defaultMessage: "Help from Expert" }),
            icon: props => <HeadsetHelp {...props} />,
            // TODO: move to SITE_CONFIG — 暂指向官网，待 Junoa 有专门的专家支持页后更新
            url: SITE_CONFIG.domains.marketing,
            activeRoutes: [],
            target: "_blank",
            isNew: true
          },
          {
            title: intl.formatMessage({ id: "layout.sidebar.moreinfo", defaultMessage: "More Info" }),
            icon: props => <MoreHorizCircle {...props} />,
            hoveredRoutes: [
              {
                hasDivider: false,
                routes: [
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.privacypolicy", defaultMessage: "Privacy Policy" }),
                    url: UrlService.privacyPolicy()
                  },
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.termsofservice", defaultMessage: "Terms of Service" }),
                    url: UrlService.termsOfService()
                  },
                  {
                    title: intl.formatMessage({ id: "layout.sidebar.contact", defaultMessage: "Contact" }),
                    url: UrlService.contact()
                  },
                  {
                    customComponent: (
                      <div className="text-muted-foreground">
                        <Separator className="my-1" />
                        <div className="px-4 py-2 text-sm">
                          <FormattedMessage
                            id="layout.sidebar.version"
                            defaultMessage="Version {version}"
                            values={{ version: process.env.NEXT_PUBLIC_APP_VERSION }}
                          />
                        </div>

                        <div className="px-4 py-2">
                          <ModeToggle />
                        </div>
                      </div>
                    )
                  }
                ]
              }
            ]
          } as ISidebarRoute
        ]
      }
    ];

    return routes;
  }, [intl]);

  const onToggleMenuClick = () => {
    onOpenMenuClick();
  };

  const onDeployClick = () => {
    setDeploySdl(null);
  };

  const drawer = (
    <div
      style={{ width: isNavOpen ? DRAWER_WIDTH : CLOSED_DRAWER_WIDTH }}
      className="box-border flex h-full flex-shrink-0 flex-col items-center justify-between overflow-y-auto overflow-x-hidden border-r-[1px] border-muted-foreground/20 bg-secondary transition-[width] duration-300 ease-in-out md:h-[calc(100%-57px)] dark:bg-card"
    >
      <div className={cn("flex w-full flex-col items-center justify-between", { ["p-4"]: isNavOpen, ["pt-4"]: !isNavOpen })}>
        <div className={cn("flex w-full items-center justify-center pt-4")}>
          {isAuthenticated && (
            <Link
              className={cn(buttonVariants({ variant: "default", size: isNavOpen ? "lg" : "icon" }), "h-9 w-full leading-4", {
                ["h-9 w-11 min-w-0 rounded-sm pb-2 pt-2"]: !isNavOpen
              })}
              href={UrlService.newDeployment()}
              onClick={onDeployClick}
              data-testid="sidebar-deploy-button"
              aria-disabled={settings.isBlockchainDown}
            >
              <Rocket className={cn("rotate-45", { ["mr-2"]: isNavOpen })} fontSize="small" />
              {isNavOpen && <FormattedMessage id="layout.sidebar.deploy" defaultMessage="Deploy" />}
            </Link>
          )}
        </div>

        {routeGroups.map((g, i) => (
          <SidebarGroupMenu key={i} group={g} hasDivider={g.hasDivider} isNavOpen={isNavOpen} />
        ))}
      </div>

      <div className={cn("flex w-full flex-col items-center justify-between", { ["p-2"]: isNavOpen, ["pb-2 pt-2"]: !isNavOpen })}>
        {extraRoutes.map((g, i) => (
          <SidebarGroupMenu key={i} group={g} hasDivider={g.hasDivider} isNavOpen={isNavOpen} />
        ))}

        {smallScreen && <MobileSidebarUser />}

        {!smallScreen && (
          <div className="flex w-full items-center justify-center pt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={onToggleMenuClick}
              className={cn("flex w-full items-center justify-start gap-3 px-4", { ["w-[45px] min-w-0 justify-center p-2"]: !isNavOpen })}
            >
              {isNavOpen ? <SidebarCollapse /> : <SidebarExpand />}
              {isNavOpen && (
                <span>
                  <FormattedMessage id="layout.sidebar.collapse" defaultMessage="Collapse" />
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <nav
      className={cn("ease fixed z-[100] bg-card md:flex-shrink-0", {
        ["md:w-[240px]"]: isNavOpen,
        ["md:w-[57px]"]: !isNavOpen
      })}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={isMobileOpen}
        disableScrollLock
        onClose={handleDrawerToggle}
        className="block p-4 md:hidden"
        ModalProps={{
          keepMounted: true // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", sm: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH, overflow: "hidden" },
          zIndex: 990
        }}
        PaperProps={{
          sx: {
            border: "none"
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        className="hidden md:block"
        variant="permanent"
        PaperProps={{
          className: cn(
            "border-none ease z-[1000] bg-card transition-[width] duration-300 box-border overflow-hidden mt-[57px]",
            {
              ["md:w-[240px]"]: isNavOpen,
              ["md:w-[57px]"]: !isNavOpen
            },
            mdDrawerClassName
          )
        }}
        open
      >
        {drawer}
      </Drawer>
    </nav>
  );
};
